import isEqual from 'lodash-es/isEqual'
import {useMemo, useState} from 'react'

import {filterFalseyValues} from '../helpers/util'
import {ViewType} from '../helpers/view-type'
import {useFindColumnByDatabaseId} from '../state-providers/columns/use-find-column-by-database-id'
import type {PaginatedMemexItemsQueryVariables} from '../state-providers/memex-items/queries/types'
import {useNextPlaceholderQuery} from '../state-providers/memex-items/queries/use-next-placeholder-query'
import {useViews} from './use-views'

/*
 * A hook to access the synthetic ids of visible fields for the current view.
 * Used to limit the fields returned in paginated items requests.
 */
export function usePaginatedVariablesWithFieldIds(
  variablesWithoutFieldIds: Omit<PaginatedMemexItemsQueryVariables, 'fieldIds'>,
): PaginatedMemexItemsQueryVariables {
  const {viewType} = variablesWithoutFieldIds
  const {currentView} = useViews()
  const {findColumnByDatabaseId} = useFindColumnByDatabaseId()
  // useViews stores fields for the current view as database ids
  const {visibleFields: visibleFieldIds, layoutSettings, sortBy} = currentView?.localViewState ?? {}
  const {dateFields, markerFields} = layoutSettings?.roadmap ?? {}
  const dateAndMarkerFields = useMemo(
    () => (dateFields?.filter(id => id !== 'none') ?? []).concat(markerFields ?? []),
    [dateFields, markerFields],
  )
  const sortByFieldIds = useMemo(() => sortBy?.map(s => s[0]), [sortBy])
  // But the server expects synthetic field ids in the request
  const nextVisibleFieldIds = useMemo(() => {
    const ids = visibleFieldIds ? [...visibleFieldIds] : []
    if (viewType !== ViewType.Roadmap && ids && sortByFieldIds) {
      ids.push(...sortByFieldIds)
    } else if (viewType === ViewType.Roadmap && ids) {
      ids.push(...dateAndMarkerFields)
    }
    const filteredIds = [
      ...new Set(filterFalseyValues(ids?.map(databaseId => findColumnByDatabaseId(databaseId)?.id) || [])),
    ]
    return filteredIds.length === 0 ? undefined : filteredIds.sort()
  }, [visibleFieldIds, viewType, sortByFieldIds, dateAndMarkerFields, findColumnByDatabaseId])

  const viewId = currentView?.id ?? -1

  // Store previously visible field IDs by view ID so that we can compare and detect changes
  const [visibleFieldIdsByView, setVisibleFieldIdsByView] = useState<
    Record<number, PaginatedMemexItemsQueryVariables['fieldIds']>
  >(() => ({[viewId]: nextVisibleFieldIds}))

  const sourceFieldIds = visibleFieldIdsByView[viewId]
  const {setUpNextPlaceholderQueries} = useNextPlaceholderQuery(
    {...variablesWithoutFieldIds, fieldIds: nextVisibleFieldIds},
    sourceFieldIds,
  )

  if (!isEqual(sourceFieldIds, nextVisibleFieldIds)) {
    if (sourceFieldIds) setUpNextPlaceholderQueries()
    setVisibleFieldIdsByView(prevState => ({
      ...prevState,
      [viewId]: nextVisibleFieldIds,
    }))
  }

  const fieldIds = visibleFieldIdsByView[viewId]

  return useMemo(
    () => ({
      ...variablesWithoutFieldIds,
      fieldIds,
    }),
    [variablesWithoutFieldIds, fieldIds],
  )
}
